import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AvatarsIntegration = () => {
  return (
    <section className="py-24 bg-gray-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
              A Platform That Works <br />
              <span className="text-indigo-400">Around Your Workflow</span>
            </h2>
            <p className="text-gray-400 text-lg mb-12 leading-relaxed">
              Automate the boring stuff. From selling tickets on Eventbrite to instant CRM updates, Avatars connects to the tools you already love without the friction.
            </p>
            <div className="space-y-8">
              {[
                { id: 1, title: "Smart Automation", desc: "Use Zapier or Pabbly to sync attendee data instantly." },
                { id: 2, title: "Secure Webhooks", desc: "Trigger real-time actions based on attendee behavior." },
                { id: 3, title: "White-Label Setup", desc: "Keep your brand front and center with custom domains." }
              ].map((step) => (
                <div key={step.id} className="flex items-start group">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600 text-white font-bold border-2 border-indigo-500 group-hover:scale-110 transition-transform">
                    {step.id}
                  </div>
                  <div className="ml-6">
                    <h4 className="text-xl font-bold text-white mb-2">{step.title}</h4>
                    <p className="text-gray-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Advanced Broadcast Hub Visualization */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-800 bg-gray-800/50 p-6 md:p-10">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent pointer-events-none"></div>
              
              <div className="relative z-10">
                {/* Main Broadcast Stage */}
                <div className="bg-gray-950 rounded-2xl border border-white/5 p-4 mb-8 relative overflow-hidden">
                  <div className="flex items-center justify-between mb-4 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">Live Broadcast</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="w-5 h-5 rounded-full border border-gray-950 bg-indigo-500/30"></div>
                        ))}
                      </div>
                      <span className="text-[10px] text-gray-400">1.2k Viewing</span>
                    </div>
                  </div>
                  
                  {/* Streaming Visual */}
                  <div className="h-32 bg-indigo-950/20 rounded-xl border border-indigo-500/20 flex items-center justify-center relative group">
                    <motion.div 
                      animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
                      transition={{ duration: 4, repeat: Infinity }}
                      className="absolute inset-0 bg-indigo-500/5"
                    />
                    <SafeIcon icon={FiIcons.FiTv} className="w-12 h-12 text-indigo-500/50 group-hover:text-indigo-400 transition-colors" />
                  </div>
                </div>

                {/* Integration Arcs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { icon: FiIcons.FiYoutube, label: 'YouTube' },
                    { icon: FiIcons.FiVideo, label: 'Vimeo' },
                    { icon: FiIcons.FiZap, label: 'Twitch' },
                    { icon: FiIcons.FiCpu, label: 'AWS' }
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-gray-900 border border-white/5 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 hover:border-indigo-500/50 transition-colors group"
                    >
                      <SafeIcon icon={item.icon} className="w-6 h-6 text-gray-500 group-hover:text-indigo-400" />
                      <span className="text-[8px] font-bold uppercase tracking-tighter text-gray-600 group-hover:text-white">{item.label}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Bottom Stats / Connectivity */}
                <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-500/10 rounded-lg">
                      <SafeIcon icon={FiIcons.FiShield} className="w-4 h-4 text-green-400" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-400">Direct Infrastructure Link</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-1 w-24 bg-gray-900 rounded-full overflow-hidden">
                      <motion.div 
                        animate={{ x: [-100, 100] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        className="h-full w-1/3 bg-indigo-500/50"
                      />
                    </div>
                    <span className="text-[10px] font-bold text-indigo-400">85% SAVINGS</span>
                  </div>
                </div>
              </div>

              {/* Decorative Floating Elements */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 -right-4 p-4 bg-gray-900 border border-white/10 rounded-2xl shadow-xl z-20"
              >
                <SafeIcon icon={FiIcons.FiMessageCircle} className="w-6 h-6 text-indigo-400" />
              </motion.div>
            </div>

            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500/20 blur-[120px] -z-0"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvatarsIntegration;